import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import {
  setSustainQueue,
  setSustainQueueTotalElements,
  SustainQueueRow,
  TicketStatus,
  transitionSustainTicketStatus,
  saveSustainQueueListState,
} from "../../../store/slices/ticketingPortalSlice";
import { fetchAllLocations, fetchSustainQueue, updateSustainTicket } from "../../../store/thunks/ticketingPortalThunk";
import { selectCategoryBetaParams } from "../../../store/selectors/appDataSelectors";
import { fetchAvailableTicketingTeams } from "../../../store/thunks/ticketingPortalThunk";
import { formatUserName } from "../../../utils/emailTouserName";
import { createApiClient } from "../../../api/apiClient";

/** Shape of GET /ticketing/form-config/categories (nested sub / tertiary categories) */
type FormConfigCategory = {
  id: number;
  name: string;
  active?: boolean;
  subCategories?: {
    id: number;
    name: string;
    active?: boolean;
    tertiaryCategories?: { id: number; name: string; active?: boolean }[];
  }[];
};

/** Filter state scoped to the Table tab */
export type TableFilters = {
  statuses: string[];
  urgencies: string[];
  categories: string[];
  subcategories: string[];
  locations: string[];
  assignees: string[];
  tertiaries: string[];
  managers: string[];
};

export const EMPTY_TABLE_FILTERS: TableFilters = {
  statuses: [],
  urgencies: [],
  categories: [],
  subcategories: [],
  locations: [],
  assignees: [],
  tertiaries: [],
  managers: [],
};

export type QueueViewMode = "kanban" | "list";

export type SustainQueueStatusPill = {
  key: "all" | TicketStatus;
  label: string;
  dotColor?: string;
};

/** Multi-select filter state for the web filter bar */
export type KanbanFilters = {
  /** multi-select — empty = all statuses visible */
  statuses: TicketStatus[];
  urgencies: string[];
  categories: string[];
  subcategories: string[];
  locations: string[];
  markets: string[];
  regions: string[];
  assignees: string[];
  /** Tertiary category names selected (resolved to IDs at fetch time) */
  tertiaries: string[];
  /** Sustain Team lead emails selected — matches tickets assigned to the lead
   *  or to any member whose manager is that lead */
  managers: string[];
};

export const EMPTY_KANBAN_FILTERS: KanbanFilters = {
  statuses: [],
  urgencies: [],
  categories: [],
  subcategories: [],
  locations: [],
  markets: [],
  regions: [],
  assignees: [],
  tertiaries: [],
  managers: [],
};

const STATUS_PILLS: SustainQueueStatusPill[] = [
  { key: "all", label: "All" },
  { key: "new", label: "New", dotColor: "#005CBC" },
  { key: "progress", label: "In Progress", dotColor: "#7871E2" },
  { key: "waiting-it", label: "Escalated to IT", dotColor: "#FCC42B" },
  { key: "waiting-tmv", label: "Escalated to TMV", dotColor: "#FCC42B" },
  { key: "submitted-ado", label: "Submitted to ADO", dotColor: "#FF8C09" },
  { key: "waiting-user", label: "Waiting on User", dotColor: "#FCC42B" },
  { key: "resolved", label: "Closed", dotColor: "#6B7280" },
];

const KANBAN_ORDER: TicketStatus[] = [
  "new",
  "progress",
  "waiting-it",
  "waiting-tmv",
  "submitted-ado",
  "waiting-user",
  "waiting-vendor",
  "resolved",
];

const SUSTAIN_QUEUE_PAGE_SIZE = 100;

/** Default Kanban visibility: hide resolved until explicitly selected */
const DEFAULT_VISIBLE_STATUSES: TicketStatus[] = [
  "new",
  "progress",
  "waiting-it",
  "waiting-tmv",
  "submitted-ado",
  "waiting-user",
  "waiting-vendor",
];

const getServerStatusesForActivePill = (
  activeStatus: "all" | TicketStatus,
  selectedStatuses: TicketStatus[]
): TicketStatus[] => {
  if (activeStatus !== "resolved") {
    return selectedStatuses;
  }

  // Closed pill should force Closed-only server fetch.
  return ["resolved"];
};

const matchesMulti = (value: string | undefined, selected: string[]): boolean =>
  selected.length === 0 || (!!value && selected.includes(value));

/**
 * Advance the Active Resolution Timer and Waiting-on-IT Timer for all
 * tickets that have an open (current) status interval.  Called every 30 s.
 */
const tickTimers = (
  queue: SustainQueueRow[],
  dispatch: AppDispatch,
  setter: typeof setSustainQueue
) => {
  const now = Date.now();
  const updated = queue.map((t) => {
    if (!t.statusEnteredAt || t.status === "resolved") return t;
    const elapsed = now - t.statusEnteredAt;
    const waitingItMs = (t.waitingItMs ?? 0) + (t.status === "waiting-it" ? elapsed : 0);
    const activeResolutionMs = (t.activeResolutionMs ?? 0) + (t.status !== "waiting-it" ? elapsed : 0);
    return {
      ...t,
      waitingItMs,
      waitingItMinutes: Math.round(waitingItMs / 60000),
      activeResolutionMs,
      minutesAging: Math.round(activeResolutionMs / 60000),
      aging: fmtDuration(activeResolutionMs),
      // Re-anchor so the next tick measures from now
      statusEnteredAt: now,
    };
  });
  dispatch(setter(updated));
};

const fmtDuration = (ms: number): string => {
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    const m = mins % 60;
    return m > 0 ? `${hours}h ${m}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  return h > 0 ? `${days}d ${h}h` : `${days}d`;
};


const useSustainQueueListing = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    sustainQueue,
    sustainQueueTotalElements,
    sustainTeamMembers,
    sustainQueueLoading,
    allLocations,
    sustainQueueListState,
  } = useSelector((state: RootState) => state.ticketingPortal);
  const currentUserEmail = useSelector(
    (state: RootState) => state.appData.userInfo?.email ?? ""
  ).toLowerCase();

  // ── Restore list state when returning from Ticket Detail ─────────────────
  // If a saved state exists in Redux, initialise local state from it and
  // clear the snapshot so it is only used once per "Back" navigation.
  const restoredState = sustainQueueListState;

  const [currentPage, setCurrentPage] = useState(() => restoredState?.currentPage ?? 1);
  const pageSize = SUSTAIN_QUEUE_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(sustainQueueTotalElements / pageSize));
  const [query, setQuery] = useState(() => restoredState?.query ?? "");
  const [activeStatus, setActiveStatus] = useState<"all" | TicketStatus>(
    () => restoredState?.activeStatus ?? "all"
  );
  const [viewMode, setViewMode] = useState<QueueViewMode>(
    () => restoredState?.viewMode ?? "kanban"
  );

  // Closed-pill behavior: entering Closed does a dedicated API fetch.
  // Leaving Closed restores the last non-closed dataset without another API call.
  const prevActiveStatusRef = useRef<"all" | TicketStatus>(restoredState?.activeStatus ?? "all");
  const preClosedSnapshotRef = useRef<{
    queue: SustainQueueRow[];
    totalElements: number;
    page: number;
  } | null>(null);
  // ── Single shared filter state for both Kanban and Table tabs ────────────
  // One source of truth means filters applied in one tab are always visible
  // and clearable in the other tab.
  const [kanbanFilters, setKanbanFilters] = useState<KanbanFilters>(
    () => ({
      ...EMPTY_KANBAN_FILTERS,
      ...(restoredState?.kanbanFilters ?? {}),
    })
  );
  
  // ── Unfiltered queue for accurate status counts ────────────────────────────
  // Store unfiltered data separately so status counts remain constant when
  // filters are applied (similar to My Tickets implementation).
  const [unfilteredQueue, setUnfilteredQueue] = useState<SustainQueueRow[]>([]);

  // ── Sticky filter-option cache ─────────────────────────────────────────────
  // Options accumulate across all API responses so dropdowns always show every
  // previously-seen value even when the current fetch is a filtered subset.
  const [allSeenOptions, setAllSeenOptions] = useState<{
    categories: string[];
    subcategories: string[];
    locations: string[];
    markets: string[];
    regions: string[];
    assignees: string[];
  }>({ categories: [], subcategories: [], locations: [], markets: [], regions: [], assignees: [] });
  const allSeenOptionsRef = useRef(allSeenOptions);
  allSeenOptionsRef.current = allSeenOptions;

  // Derive tableFilters from the shared state so the Table filter bar reflects
  // whatever was applied in Kanban (and vice-versa).
  const tableFilters: TableFilters = useMemo(() => ({
    statuses: kanbanFilters.statuses,
    urgencies: kanbanFilters.urgencies,
    categories: kanbanFilters.categories,
    subcategories: kanbanFilters.subcategories,
    // Combine all three location buckets into the single table location field.
    locations: [...kanbanFilters.locations, ...kanbanFilters.markets, ...kanbanFilters.regions],
    assignees: kanbanFilters.assignees,
    tertiaries: kanbanFilters.tertiaries,
    managers: kanbanFilters.managers,
  }), [kanbanFilters]);

  // ── Team View: Sustain Teams lookup + tertiary category lookup ───────────
  // Lightweight local-state fetches (same "just a lookup list" pattern used by
  // SustainTeamManagementScreen) — avoids pulling in the heavier useTicketingConfig
  // hook which also fetches team-assignments/category-mapping CRUD state.
  const { availableTicketingTeams: teams } = useSelector(
    (state: RootState) => state.ticketingPortal
  );
  const [tertiaryNameToId, setTertiaryNameToId] = useState<Record<string, number>>({});
  const [tertiaryOptions, setTertiaryOptions] = useState<string[]>([]);
  const [configCategoryOptions, setConfigCategoryOptions] = useState<string[]>([]);
  const [configSubcategoryOptions, setConfigSubcategoryOptions] = useState<string[]>([]);

  const [teamsSettled, setTeamsSettled] = useState(false);

  const refreshTeams = useCallback(() => {
    return dispatch(fetchAvailableTicketingTeams());
  }, [dispatch]);

  useEffect(() => {
    Promise.resolve(refreshTeams())
      .catch(() => undefined)
      .finally(() => setTeamsSettled(true));
  }, [refreshTeams]);

  const categoryBetaParams = useSelector(selectCategoryBetaParams);

  // Category filter options depend on location/role (beta-filtered category tree).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const api = createApiClient();
        const res = await api.get<FormConfigCategory[]>("/ticketing/form-config/categories", { params: categoryBetaParams });
        if (cancelled) return;
        const categories = Array.isArray(res.data) ? res.data : [];
        const isActive = (x: { active?: boolean }) => x.active !== false;
        const activeCategories = categories.filter(isActive);
        const subs = activeCategories.flatMap((c) => (c.subCategories ?? []).filter(isActive));
        const tertiaries = subs.flatMap((s) => (s.tertiaryCategories ?? []).filter(isActive));

        setConfigCategoryOptions(
          [...new Set(activeCategories.map((c) => c.name).filter(Boolean))].sort()
        );
        setConfigSubcategoryOptions(
          [...new Set(subs.map((s) => s.name).filter(Boolean))].sort()
        );
        const nameToId: Record<string, number> = {};
        tertiaries.forEach((t) => { nameToId[t.name] = t.id; });
        setTertiaryNameToId(nameToId);
        setTertiaryOptions(Object.keys(nameToId).sort());
      } catch {
        // fall back to options derived from queue data
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [categoryBetaParams]);

  /** TKT_Teams.id the logged-in user belongs to, resolved from the sustain roster (null = none/unknown). */
  const myTeamId = useMemo(() => {
    const me = sustainTeamMembers.find((m) => m.userEmail.toLowerCase() === currentUserEmail);
    return me?.teamId ?? null;
  }, [sustainTeamMembers, currentUserEmail]);

  const [viewingTeamId, setViewingTeamIdState] = useState<number | null | undefined>(undefined);

  // Resolve the default Team View scope once teams + the user's own team are known:
  // sustain members default to their own team; everyone else (Super Users) defaults
  // to "PBNA Business" per the user story, falling back to the first team.
  useEffect(() => {
    if (viewingTeamId !== undefined) return;
    if (teams.length === 0) {
      // Team catalog failed/empty: stop waiting so the queue still loads (unscoped).
      if (teamsSettled) setViewingTeamIdState(null);
      return;
    }
    if (myTeamId != null) {
      setViewingTeamIdState(myTeamId);
      return;
    }
    const pbnaBusiness = teams.find((t) => /pbna\s*business/i.test(t.name));
    setViewingTeamIdState(pbnaBusiness?.id ?? teams[0]?.id ?? null);
  }, [teams, myTeamId, viewingTeamId, teamsSettled]);

  const setViewingTeamId = useCallback((teamId: number) => {
    setViewingTeamIdState(teamId);
    setCurrentPage(1);
    setKanbanFilters((prev) => ({ ...prev, managers: [] }));
  }, []);

  /** false while viewing another team's read-only Sustain Queue */
  const isViewingOwnTeam = myTeamId != null && viewingTeamId === myTeamId;

  /** Sustain Team Leads for the team currently being viewed — options for the Manager filter. */
  const managerOptions = useMemo(() => {
    if (viewingTeamId == null) return [] as { email: string; label: string }[];
    return sustainTeamMembers
      .filter((m) => (m.role ?? "").toLowerCase() === "lead" && m.teamId === viewingTeamId)
      .map((m) => ({ email: m.userEmail, label: formatUserName(m.userEmail) }));
  }, [sustainTeamMembers, viewingTeamId]);

  const resolveTertiaryIds = useCallback(
    (names: string[]): string[] =>
      names.map((n) => tertiaryNameToId[n]).filter((v): v is number => v != null).map(String),
    [tertiaryNameToId]
  );

  const viewingTeamParam = useMemo(
    () => (viewingTeamId != null ? [String(viewingTeamId)] : undefined),
    [viewingTeamId]
  );

  /** True when a ticket's assignee is (or reports to) one of the selected Manager filter leads. */
  const matchesManagerFilter = useCallback(
    (ticket: SustainQueueRow, selectedManagers: string[]): boolean => {
      if (selectedManagers.length === 0) return true;
      if (ticket.assignedTo === "Unassigned") return false;
      const member = sustainTeamMembers.find((m) => formatUserName(m.userEmail) === ticket.assignedTo);
      if (!member) return false;
      if (selectedManagers.includes(member.userEmail)) return true;
      return !!member.managerEmail && selectedManagers.includes(member.managerEmail);
    },
    [sustainTeamMembers]
  );

  // Derive Location / Market / Region option lists from the allLocations API
  // response so filters are populated even before the queue is loaded.
  const apiFilterOptions = useMemo(() => {
    if (allLocations.length === 0) return null;
    const locs = [...new Set(allLocations.map(l => l.locName).filter(Boolean))].sort();
    const markets = [...new Set(allLocations.map(l => l.market).filter(Boolean))].sort();
    const regions = [...new Set(allLocations.map(l => l.region).filter(Boolean))].sort();
    return { locations: locs, markets, regions };
  }, [allLocations]);

  // When the Table tab writes filters back, funnel them into the shared state.
  // Each location value is routed into the correct Kanban bucket (Location /
  // Market / Region) using the sticky cache so Kanban shows the right dropdown
  // as pre-selected after a tab switch.
  const setTableFilters = useCallback((f: TableFilters) => {
    const marketSet = apiFilterOptions
      ? new Set(apiFilterOptions.markets)
      : new Set(allSeenOptionsRef.current.markets);
    const regionSet = apiFilterOptions
      ? new Set(apiFilterOptions.regions)
      : new Set(allSeenOptionsRef.current.regions);
    setKanbanFilters(prev => ({
      ...prev,
      statuses: f.statuses as TicketStatus[],
      urgencies: f.urgencies,
      categories: f.categories,
      subcategories: f.subcategories,
      locations: f.locations.filter(v => !marketSet.has(v) && !regionSet.has(v)),
      markets: f.locations.filter(v => marketSet.has(v)),
      regions: f.locations.filter(v => regionSet.has(v)),
      assignees: f.assignees,
      tertiaries: f.tertiaries,
      managers: f.managers,
    }));
  }, []);

  // ── Initial fetch (once the Team View scope is resolved) ──────────────────
  // Waiting for viewingTeamId guarantees the first request already carries
  // assignedTeam — no unscoped call followed by a team-scoped one.
  // When returning from Ticket Detail, restore the previously-saved page and
  // filters; on a fresh visit start from page 1 with no filters.
  // The Redux snapshot is consumed on mount so it's only applied once.
  const restoredStateRef = useRef(restoredState);
  const initialFetchDoneRef = useRef(false);
  const lastFetchKeyRef = useRef<string | null>(null);

  // Everything (besides page / status pill) that triggers a re-fetch.
  const filterFetchKey = useMemo(
    () =>
      JSON.stringify({
        query: query.trim(),
        kanbanFilters,
        tertiaryIds: resolveTertiaryIds(kanbanFilters.tertiaries),
        teams: viewingTeamParam,
      }),
    [query, kanbanFilters, resolveTertiaryIds, viewingTeamParam]
  );

  useEffect(() => {
    if (restoredStateRef.current) {
      dispatch(saveSustainQueueListState(null)); // consume the snapshot
    }
    dispatch(fetchAllLocations());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (viewingTeamId === undefined || initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    lastFetchKeyRef.current = filterFetchKey;
    const restored = restoredStateRef.current;
    if (restored) {
      dispatch(
        fetchSustainQueue({
          search: restored.query.trim() || undefined,
          statuses: getServerStatusesForActivePill(
            restored.activeStatus,
            restored.kanbanFilters.statuses
          ),
          urgencies: restored.kanbanFilters.urgencies,
          categories: restored.kanbanFilters.categories,
          subcategories: restored.kanbanFilters.subcategories,
          locations: [
            ...restored.kanbanFilters.locations,
            ...restored.kanbanFilters.markets,
            ...restored.kanbanFilters.regions,
          ],
          assignees: restored.kanbanFilters.assignees,
          tertiaries: resolveTertiaryIds(restored.kanbanFilters.tertiaries ?? []),
          managers: restored.kanbanFilters.managers ?? [],
          teams: viewingTeamParam,
          pageNumber: restored.currentPage,
          pageSize,
        })
      );
    } else {
      dispatch(fetchSustainQueue({ pageNumber: 1, pageSize, teams: viewingTeamParam }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingTeamId]);

  // ── Update unfiltered queue when filters are not active ───────────────────
  // Store the initial unfiltered data for status count calculations
  const hasActiveFilters = useMemo(() => 
    query.trim() ||
    kanbanFilters.statuses.length > 0 ||
    kanbanFilters.urgencies.length > 0 ||
    kanbanFilters.categories.length > 0 ||
    kanbanFilters.subcategories.length > 0 ||
    kanbanFilters.locations.length > 0 ||
    kanbanFilters.markets.length > 0 ||
    kanbanFilters.regions.length > 0 ||
    kanbanFilters.assignees.length > 0 ||
    kanbanFilters.tertiaries.length > 0 ||
    kanbanFilters.managers.length > 0
  , [query, kanbanFilters]);

  useEffect(() => {
    // Only update unfiltered queue when no filters are active
    if (!hasActiveFilters && sustainQueue.length > 0) {
      setUnfilteredQueue(sustainQueue);
    }
  }, [sustainQueue, hasActiveFilters]);

  // While Closed pill is active, status filter chips in the lower filter bar
  // should be cleared so UI communicates Closed-only mode.
  useEffect(() => {
    if (activeStatus === "resolved" && kanbanFilters.statuses.length > 0) {
      setKanbanFilters((prev) => ({ ...prev, statuses: [] }));
    }
  }, [activeStatus, kanbanFilters.statuses.length]);

  // ── Re-fetch whenever shared filters change ───────────────────────────────
  useEffect(() => {
    // Skip until the initial fetch ran, and skip when nothing changed since the
    // last fetch (e.g. the render where the team scope resolved).
    if (!initialFetchDoneRef.current || lastFetchKeyRef.current === filterFetchKey) return;
    // Debounce so rapid typing doesn't flood the API; filter selections feel
    // instant at 350 ms.
    const timer = setTimeout(() => {
      lastFetchKeyRef.current = filterFetchKey;
      setCurrentPage(1);
      dispatch(
        fetchSustainQueue({
          search: query.trim() || undefined,
          statuses: getServerStatusesForActivePill(activeStatus, kanbanFilters.statuses),
          urgencies: kanbanFilters.urgencies,
          categories: kanbanFilters.categories,
          subcategories: kanbanFilters.subcategories,
          locations: [
            ...kanbanFilters.locations,
            ...kanbanFilters.markets,
            ...kanbanFilters.regions,
          ],
          assignees: kanbanFilters.assignees,
          tertiaries: resolveTertiaryIds(kanbanFilters.tertiaries),
          managers: kanbanFilters.managers,
          teams: viewingTeamParam,
          pageNumber: 1,
          pageSize,
        })
      );
    }, 350);
    return () => clearTimeout(timer);
  }, [query, kanbanFilters, dispatch, pageSize, viewingTeamParam, resolveTertiaryIds, filterFetchKey]);

  // ── Status pill special-case: Closed should explicitly fetch from API ───
  useEffect(() => {
    const prevStatus = prevActiveStatusRef.current;

    if (activeStatus === "resolved" && prevStatus !== "resolved") {
      preClosedSnapshotRef.current = {
        queue: sustainQueue,
        totalElements: sustainQueueTotalElements,
        page: currentPage,
      };

      setCurrentPage(1);
      dispatch(
        fetchSustainQueue({
          search: query.trim() || undefined,
          statuses: getServerStatusesForActivePill(activeStatus, kanbanFilters.statuses),
          urgencies: kanbanFilters.urgencies,
          categories: kanbanFilters.categories,
          subcategories: kanbanFilters.subcategories,
          locations: [
            ...kanbanFilters.locations,
            ...kanbanFilters.markets,
            ...kanbanFilters.regions,
          ],
          assignees: kanbanFilters.assignees,
          tertiaries: resolveTertiaryIds(kanbanFilters.tertiaries),
          managers: kanbanFilters.managers,
          teams: viewingTeamParam,
          pageNumber: 1,
          pageSize,
        })
      );
    }

    if (activeStatus !== "resolved" && prevStatus === "resolved") {
      if (activeStatus === "all") {
        // Closed -> All should re-fetch the default list (no status filter).
        setCurrentPage(1);
        dispatch(
          fetchSustainQueue({
            search: query.trim() || undefined,
            statuses: getServerStatusesForActivePill(activeStatus, kanbanFilters.statuses),
            urgencies: kanbanFilters.urgencies,
            categories: kanbanFilters.categories,
            subcategories: kanbanFilters.subcategories,
            locations: [
              ...kanbanFilters.locations,
              ...kanbanFilters.markets,
              ...kanbanFilters.regions,
            ],
            assignees: kanbanFilters.assignees,
            tertiaries: resolveTertiaryIds(kanbanFilters.tertiaries),
            managers: kanbanFilters.managers,
            teams: viewingTeamParam,
            pageNumber: 1,
            pageSize,
          })
        );
      } else {
        const snapshot = preClosedSnapshotRef.current;
        if (snapshot) {
          dispatch(setSustainQueue(snapshot.queue));
          dispatch(setSustainQueueTotalElements(snapshot.totalElements));
          setCurrentPage(snapshot.page);
        }
      }
      preClosedSnapshotRef.current = null;
    }

    prevActiveStatusRef.current = activeStatus;
  }, [
    activeStatus,
    sustainQueue,
    sustainQueueTotalElements,
    currentPage,
    query,
    kanbanFilters,
    dispatch,
    pageSize,
    viewingTeamParam,
    resolveTertiaryIds,
  ]);

  // ── Auto-tick timers every 30 s ───────────────────────────────────────────
  const sustainQueueRef = useRef(sustainQueue);
  sustainQueueRef.current = sustainQueue;

  useEffect(() => {
    const pollInterval = setInterval(() => {
      tickTimers(sustainQueueRef.current, dispatch, setSustainQueue);
    }, 30_000);

    return () => clearInterval(pollInterval);
  }, [dispatch]);

  // ── Grow the options cache whenever queue data changes ───────────────────
  // Runs after every API response (filtered or not) so the dropdown always
  // shows every previously-seen value — not just what the current fetch returned.
  useEffect(() => {
    if (sustainQueue.length === 0) return;
    setAllSeenOptions(prev => {
      const merge = (existing: string[], newVals: (string | undefined | null)[]) =>
        [...new Set([...existing, ...(newVals.filter(Boolean) as string[])])].sort();
      return {
        categories: merge(prev.categories, sustainQueue.map(t => t.category)),
        subcategories: merge(prev.subcategories, sustainQueue.map(t => t.subcategory)),
        locations: merge(prev.locations, sustainQueue.filter(t => t.ticketLevel === "Location").map(t => t.location)),
        markets: merge(prev.markets, sustainQueue.filter(t => t.ticketLevel === "Market").map(t => t.location)),
        regions: merge(prev.regions, sustainQueue.filter(t => t.ticketLevel === "Region").map(t => t.location)),
        assignees: merge(prev.assignees, sustainQueue.map(t => t.assignedTo).filter(a => a && a !== "Unassigned")),
      };
    });
  }, [sustainQueue]);

  const statusCounts = useMemo(() => {
    // When filters are active, reflect the filtered state so pills update accordingly.
    // "all" always uses sustainQueueTotalElements (server-side total for current filters).
    // Per-status counts use the current sustainQueue page when filters are active,
    // and the unfiltered snapshot otherwise.
    const dataForCounts = hasActiveFilters
      ? sustainQueue
      : unfilteredQueue.length > 0
        ? unfilteredQueue
        : sustainQueue;
    const counts: Record<string, number> = { all: sustainQueueTotalElements };
    STATUS_PILLS.forEach((pill) => {
      if (pill.key !== "all") {
        counts[pill.key] = dataForCounts.filter((ticket) => ticket.status === pill.key).length;
      }
    });
    return counts;
  }, [unfilteredQueue, sustainQueue, hasActiveFilters, sustainQueueTotalElements]);

  /** Which status columns are currently visible on the Kanban board */
  const visibleStatuses: TicketStatus[] = useMemo(() => {
    // Status pill click takes priority — narrows the board to that one status
    if (activeStatus !== "all") return [activeStatus];
    // No specific pill selected: respect filter-bar status selections or default columns
    if (kanbanFilters.statuses.length > 0) return kanbanFilters.statuses;
    return DEFAULT_VISIBLE_STATUSES;
  }, [kanbanFilters.statuses, activeStatus]);

  const filteredTickets = useMemo(() => {
    const q = query.trim().toLowerCase();

    return sustainQueue.filter((ticket) => {
      // Global search — OR across fields
      if (q) {
        const searchable = [ticket.id, ticket.title, ticket.description, ticket.submittedBy ?? ""]
          .join(" ")
          .toLowerCase();
        if (!searchable.includes(q)) return false;
      }

      // Status filter (AND with other filters)
      if (!visibleStatuses.includes(ticket.status)) return false;

      // Multi-select filters — AND across types, OR within each type
      if (!matchesMulti(ticket.urgency, kanbanFilters.urgencies)) return false;
      if (!matchesMulti(ticket.category, kanbanFilters.categories)) return false;
      if (!matchesMulti(ticket.subcategory, kanbanFilters.subcategories)) return false;
      // Location / Market / Region all map to ticket.location (levelReferenceId from API).
      // Combine all three selections into one check so selecting any of them works correctly.
      const allLocationFilters = [
        ...kanbanFilters.locations,
        ...kanbanFilters.markets,
        ...kanbanFilters.regions,
      ];
      // When location filters are active, use strict matching
      // When no location filters selected, show all (including National and COE)
      if (allLocationFilters.length > 0 && !matchesMulti(ticket.location, allLocationFilters)) return false;
      if (!matchesMulti(ticket.assignedTo, kanbanFilters.assignees)) return false;
      if (!matchesMulti(ticket.tertiary, kanbanFilters.tertiaries)) return false;
      if (!matchesManagerFilter(ticket, kanbanFilters.managers)) return false;

      return true;
    });
  }, [sustainQueue, query, visibleStatuses, kanbanFilters, matchesManagerFilter]);

  /**
   * Table view status behavior:
   * 1) Respect explicit status selections from the table filter bar.
   * 2) If none selected, respect the global status pill (activeStatus).
   * 3) If global pill is "all", show all statuses.
   */
  const filteredTicketsForTable = useMemo(() => {
    const q = query.trim().toLowerCase();
    const explicitStatuses = kanbanFilters.statuses; // user-selected, may be empty
    // Status pill click takes priority over filter-bar status selections
    const effectiveStatuses: TicketStatus[] =
      activeStatus !== "all"
        ? [activeStatus]
        : explicitStatuses.length > 0
          ? explicitStatuses
          : [];

    return sustainQueue.filter((ticket) => {
      if (q) {
        const searchable = [ticket.id, ticket.title, ticket.description, ticket.submittedBy ?? ""]
          .join(" ")
          .toLowerCase();
        if (!searchable.includes(q)) return false;
      }

      if (effectiveStatuses.length > 0 && !effectiveStatuses.includes(ticket.status)) return false;

      if (!matchesMulti(ticket.urgency, kanbanFilters.urgencies)) return false;
      if (!matchesMulti(ticket.category, kanbanFilters.categories)) return false;
      if (!matchesMulti(ticket.subcategory, kanbanFilters.subcategories)) return false;
      const allLocationFilters = [
        ...kanbanFilters.locations,
        ...kanbanFilters.markets,
        ...kanbanFilters.regions,
      ];
      // When location filters are active, use strict matching
      // When no location filters selected, show all (including National and COE)
      if (allLocationFilters.length > 0 && !matchesMulti(ticket.location, allLocationFilters)) return false;
      if (!matchesMulti(ticket.assignedTo, kanbanFilters.assignees)) return false;
      if (!matchesMulti(ticket.tertiary, kanbanFilters.tertiaries)) return false;
      if (!matchesManagerFilter(ticket, kanbanFilters.managers)) return false;

      return true;
    });
  }, [sustainQueue, query, kanbanFilters, activeStatus, matchesManagerFilter]);

  const groupedForKanban = useMemo(() => {
    const byStatus: Partial<Record<TicketStatus, SustainQueueRow[]>> & Record<string, SustainQueueRow[]> = {
      new: [],
      progress: [],
      "waiting-it": [],
      "waiting-tmv": [],
      "submitted-ado": [],
      "waiting-user": [],
      "waiting-vendor": [],
      resolved: [],
      "closed-merged": [],
    };

    filteredTickets.forEach((ticket) => {
      if (byStatus[ticket.status]) byStatus[ticket.status]!.push(ticket);
    });

    // Only include columns that are in visibleStatuses, in the canonical order
    return KANBAN_ORDER
      .filter((statusKey) => visibleStatuses.includes(statusKey))
      .map((statusKey) => ({ statusKey, tickets: byStatus[statusKey] ?? [] }));
  }, [filteredTickets, visibleStatuses]);

  const subtitle = useMemo(() => {
    // Use unfiltered data for accurate counts that don't change with filters
    const dataForSubtitle = unfilteredQueue.length > 0 ? unfilteredQueue : sustainQueue;
    const activeTickets = dataForSubtitle.filter((ticket) => ticket.status !== "resolved").length;
    const unassigned = dataForSubtitle.filter((ticket) => ticket.assignedTo === "Unassigned").length;
    // Use totalDuplicateCount from API (same across all tickets in response)
    const duplicates = sustainQueue.length > 0 ? (sustainQueue[0].totalDuplicateCount ?? 0) : 0;
    return `${activeTickets} active tickets · ${unassigned} unassigned · ${duplicates} possible duplicates`;
  }, [unfilteredQueue, sustainQueue, sustainQueueTotalElements]);

  const currentQueueFetchFilters = useMemo(() => ({
    search: query.trim() || undefined,
    statuses: getServerStatusesForActivePill(activeStatus, kanbanFilters.statuses),
    urgencies: kanbanFilters.urgencies,
    categories: kanbanFilters.categories,
    subcategories: kanbanFilters.subcategories,
    locations: [
      ...kanbanFilters.locations,
      ...kanbanFilters.markets,
      ...kanbanFilters.regions,
    ],
    assignees: kanbanFilters.assignees,
    tertiaries: resolveTertiaryIds(kanbanFilters.tertiaries),
    managers: kanbanFilters.managers,
    teams: viewingTeamParam,
    pageNumber: currentPage,
    pageSize,
  }), [query, kanbanFilters, activeStatus, currentPage, pageSize, resolveTertiaryIds, viewingTeamParam]);

  const moveTicketToStatus = (ticketId: string, nextStatus: TicketStatus) => {
    // Optimistic local update for instant UI feedback
    dispatch(transitionSustainTicketStatus({ ticketId, nextStatus, nowMs: Date.now() }));
    // PUT to backend, then re-fetch queue to sync
    void dispatch(updateSustainTicket(ticketId, { status: nextStatus }, currentQueueFetchFilters))
      .then(refreshTeams);
  };

  const quickUpdateTicket = useCallback((
    ticketId: string,
    updates: Partial<Pick<SustainQueueRow, "title" | "urgency" | "status" | "assignedTo">>
  ) => {
    // Optimistic local update for instant UI feedback
    if (updates.status !== undefined) {
      dispatch(transitionSustainTicketStatus({ ticketId, nextStatus: updates.status, nowMs: Date.now() }));
      const rest = { ...updates };
      delete rest.status;
      if (Object.keys(rest).length > 0) {
        const nextQueue = sustainQueue.map((t) =>
          t.id === ticketId ? { ...t, ...rest } : t
        );
        dispatch(setSustainQueue(nextQueue));
      }
    } else {
      const nextQueue = sustainQueue.map((t) =>
        t.id === ticketId ? { ...t, ...updates } : t
      );
      dispatch(setSustainQueue(nextQueue));
    }
    // PUT to backend for status / urgency / assignedTo changes, then re-fetch
    const apiUpdates: Partial<Pick<SustainQueueRow, "status" | "urgency" | "assignedTo">> = {};
    if (updates.status !== undefined) apiUpdates.status = updates.status;
    if (updates.urgency !== undefined) apiUpdates.urgency = updates.urgency;
    if (updates.assignedTo !== undefined) apiUpdates.assignedTo = updates.assignedTo;
    if (Object.keys(apiUpdates).length > 0) {
      void dispatch(updateSustainTicket(ticketId, apiUpdates, currentQueueFetchFilters))
        .then(refreshTeams);
    }
  }, [dispatch, sustainQueue, currentQueueFetchFilters, refreshTeams]);

  /** Navigate to a specific page */
  const goToPage = useCallback((page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    dispatch(
      fetchSustainQueue({
        search: query.trim() || undefined,
        statuses: getServerStatusesForActivePill(activeStatus, kanbanFilters.statuses),
        urgencies: kanbanFilters.urgencies,
        categories: kanbanFilters.categories,
        subcategories: kanbanFilters.subcategories,
        locations: [
          ...kanbanFilters.locations,
          ...kanbanFilters.markets,
          ...kanbanFilters.regions,
        ],
        assignees: kanbanFilters.assignees,
        tertiaries: resolveTertiaryIds(kanbanFilters.tertiaries),
        managers: kanbanFilters.managers,
        teams: viewingTeamParam,
        pageNumber: page,
        pageSize,
      })
    );
  }, [dispatch, totalPages, query, kanbanFilters, activeStatus, pageSize, resolveTertiaryIds, viewingTeamParam]);

  // Assignable-users list: sustain team members (pre-fetched in Redux by
  // TicketingPortalScreen) merged with any additional names seen in queue data.
  // No extra API call is made here — Redux already holds the data.
  const assignableUsers = useMemo(() => {
    const teamNames = sustainTeamMembers.map((m) => formatUserName(m.userEmail));
    const merged = ["Unassigned", ...teamNames, ...allSeenOptions.assignees];
    return merged.filter((name, i, arr) => arr.indexOf(name) === i);
  }, [sustainTeamMembers, allSeenOptions.assignees]);

  /** Filter option lists — Location/Market/Region come from the all-locations
   *  API when available, falling back to the sticky queue data cache.
   *  Special values "National" and "COE" are always included in regions filter. */
  const filterOptions = useMemo(() => ({
    categories: configCategoryOptions.length > 0 ? configCategoryOptions : allSeenOptions.categories,
    subcategories: configSubcategoryOptions.length > 0 ? configSubcategoryOptions : allSeenOptions.subcategories,
    locations: apiFilterOptions?.locations ?? allSeenOptions.locations,
    markets: apiFilterOptions?.markets ?? allSeenOptions.markets,
    regions: [
      "COE",
      "National",
      ...(apiFilterOptions?.regions ?? allSeenOptions.regions),
    ],
    tertiaries: tertiaryOptions,
  }), [allSeenOptions, apiFilterOptions, tertiaryOptions, configCategoryOptions, configSubcategoryOptions]);

  return {
    sustainQueueLoading,
    query,
    activeStatus,
    viewMode,
    kanbanFilters,
    subtitle,
    totalCount: sustainQueueTotalElements,
    filteredTickets,
    filteredTicketsForTable,
    groupedForKanban,
    assignableUsers,
    filterOptions,
    statusPills: STATUS_PILLS,
    statusCounts,
    visibleStatuses,
    setQuery,
    setActiveStatus,
    setViewMode,
    setKanbanFilters,
    tableFilters,
    setTableFilters,
    moveTicketToStatus,
    quickUpdateTicket,
    currentPage,
    totalPages,
    pageSize,
    goToPage,
    // ── Team View ────────────────────────────────────────────────────────
    teams,
    viewingTeamId: viewingTeamId ?? null,
    setViewingTeamId,
    isViewingOwnTeam,
    managerOptions,
    matchesManagerFilter,
    refreshTeams,
  };
};

export default useSustainQueueListing;
